import { Link, useParams } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { getProfileUrl } from '@/types/profile';

interface ProfileLinkProps {
  userId?: string;
  businessId?: string;
  image?: string;
  name?: string;
  showAvatar?: boolean;
  showName?: boolean;
  avatarSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  rounded?: 'full' | 'xl' | '2xl' | 'lg';
  className?: string;
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
}

const avatarSizes = {
  xs: 'h-6 w-6',
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-16 w-16',
  xl: 'h-24 w-24',
};

export function ProfileLink({
  userId,
  businessId,
  image,
  name,
  showAvatar = true,
  showName = true,
  avatarSize = 'md',
  className,
  children,
  onClick,
  rounded = 'full',
}: ProfileLinkProps) {
  const { communityId } = useParams();

  if (!userId && !businessId) {
    return <div className={className}>{children}</div>;
  }

  let href = getProfileUrl({ userId, businessId });

  // If we are in a community context, preserve the layout by prepending the communityId
  if (communityId && href.startsWith('/')) {
    href = `/${communityId}${href}`;
  }

  const initials = name
    ? name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?';

  const roundedClass =
    {
      full: 'rounded-full',
      xl: 'rounded-xl',
      '2xl': 'rounded-2xl',
      lg: 'rounded-lg',
    }[rounded] || 'rounded-full';

  return (
    <Link
      to={href}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 hover:opacity-80 transition-all duration-200 cursor-pointer group',
        className
      )}
    >
      {showAvatar && (
        <Avatar
          className={cn(
            avatarSizes[avatarSize],
            roundedClass,
            'ring-2 ring-primary/5 group-hover:ring-primary/20 transition-all'
          )}
        >
          <AvatarImage src={image} className="object-cover" />
          <AvatarFallback
            className={cn(
              'bg-gradient-to-br from-primary/10 to-primary/5 text-primary font-bold',
              roundedClass,
              avatarSize === 'xl' ? 'text-2xl' : avatarSize === 'lg' ? 'text-lg' : 'text-xs'
            )}
          >
            {initials}
          </AvatarFallback>
        </Avatar>
      )}
      {showName && (
        <span className="text-sm font-semibold group-hover:text-primary transition-colors">
          {children || name}
        </span>
      )}
      {!showName && children}
    </Link>
  );
}
